import React, { useEffect, useState, useCallback } from "react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import styles from "./SubscriptionModal.module.css";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { useInvalidateCache } from "@/utils/hooks/useInvalidateCache";
import { showApiErrorModal } from "@/utils/common/apiErrorHelper";

type Product = { id: string; name: string; price?: number | string };
type Customer = {
  id: string;
  name?: string | null;
  email?: string;
  legalRepresentativeName?: string | null;
};

interface CreateSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  customer?: Customer;
}

export default function CreateSubscriptionModal({
  open,
  onClose,
  onCreated,
  customer,
}: CreateSubscriptionModalProps) {
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const modal = useGlobalModal();
  const { invalidateSubscriptions } = useInvalidateCache();

  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [status, setStatus] = useState<string>("ACTIVE");

  useEffect(() => {
    if (customer) {
      setSelectedCustomer(customer);
      setCustomerInputValue(customer.name!! || customer.email!!);
    }
  }, [customer]);

  useEffect(() => {
    if (!open || !token) return;

    let mounted = true;
    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const resp = await api.get("/product", {
          params: { page: 1, pageSize: 500 },
          headers: { Authorization: `Bearer ${token}` },
        });
        const raw = resp && (resp as any).data ? (resp as any).data : resp;
        let list: Product[] = [];
        if (Array.isArray(raw.items)) {
          list = raw.items;
        } else if (Array.isArray(raw)) {
          list = raw;
        }
        if (mounted) setProducts(list);
      } catch (err) {
        console.error("Erro ao buscar produtos:", err);
      } finally {
        if (mounted) setProductsLoading(false);
      }
    };

    fetchProducts();
    return () => {
      mounted = false;
    };
  }, [open, token]);

  const searchCustomers = useCallback(async (query: string) => {
    if (!token) return;
    setCustomerLoading(true);
    try {
      const resp = await api.get("/customer/search-affiliates", {
        params: { query, limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = resp && (resp as any).data ? (resp as any).data : resp;
      const list = Array.isArray(raw) ? raw : [];
      setCustomers(list);
    } catch (err) {
      console.error("Erro ao buscar clientes:", err);
      setCustomers([]);
    } finally {
      setCustomerLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(() => {
      searchCustomers(customerInputValue);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [customerInputValue, open, searchCustomers]);

  useEffect(() => {
    if (open) {
      setSelectedCustomer(null);
      setCustomerInputValue("");
      setSelectedProduct(null);
      setStartDate("");
      setEndDate("");
      setStatus("ACTIVE");
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingFields: string[] = [];
    if (!selectedCustomer) missingFields.push("Cliente");
    if (!selectedProduct) missingFields.push("Produto");
    if (!startDate) missingFields.push("Data de Início");
    if (!endDate) missingFields.push("Data de Término");

    if (missingFields.length > 0) {
      const message = `Campos obrigatórios faltando: ${missingFields.join(", ")}`;
      if (modal) {
        modal.show({ type: "warning", title: "Campos obrigatórios", message, autoClose: 3000 });
      } else {
        window.alert(message);
      }
      return;
    }

    if (startDate && endDate && endDate < startDate) {
      const message = "A data de termino precisa ser igual ou posterior a data de inicio.";
      if (modal) {
        modal.show({ type: "warning", title: "Datas invalidas", message, autoClose: 3000 });
      } else {
        window.alert(message);
      }
      return;
    }

    try {
      setSaving(true);
      const body = {
        customerId: selectedCustomer!.id,
        productId: selectedProduct!.id,
        startDate,
        endDate,
        status,
      };

      await api.post("/subscriptions", body, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      const successMessage = "Inscrição criada com sucesso!";
      if (modal) {
        modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 2500 });
      } else {
        window.alert(successMessage);
      }

      await invalidateSubscriptions();
      onCreated?.();
      onClose();
    } catch (err: any) {
      console.error("Erro ao criar inscrição:", err);
      const message = showApiErrorModal(modal, err, {
        title: "Erro ao criar inscricao",
        fallbackMessage: "Erro ao criar inscricao",
      });
      if (!modal) {
        window.alert(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent} style={{ maxWidth: 600 }}>
        <div className={styles.flexSeparator}>
          <h3 className={styles.modalTitle}>Nova Inscrição</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={styles.closeButton}
          >
            x
          </button>
        </div>

        <form className={styles.clienteExpandAreaForm} onSubmit={handleSubmit}>
          <div className={styles.formFieldsContainer}>
            <label>
              <span>Cliente<span style={{ color: "#e53935" }}>*</span>:</span>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => {
                  const parts = [];
                  if (option.name) parts.push(option.name);
                  if (option.legalRepresentativeName) parts.push(option.legalRepresentativeName);
                  parts.push(option.email);
                  return parts.join(" - ");
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCustomer}
                onChange={(_, newValue) => setSelectedCustomer(newValue)}
                inputValue={customerInputValue}
                onInputChange={(_, newInputValue) => setCustomerInputValue(newInputValue)}
                loading={customerLoading}
                disabled={!!customer}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {option.name && <span style={{ fontWeight: 600 }}>{option.name}</span>}
                      <span style={{ fontWeight: 500 }}>
                        {option.legalRepresentativeName || "Sem representante"}
                      </span>
                      <span style={{ fontSize: "0.85em", opacity: 0.8 }}>{option.email}</span>
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar cliente por nome ou email..."
                    variant="outlined"
                    size="small"
                    className={styles.muiFieldInput}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="Nenhum cliente encontrado"
                loadingText="Buscando..."
                sx={{ width: "100%" }}
              />
            </label>

            <label>
              <span>Produto<span style={{ color: "#e53935" }}>*</span>:</span>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => {
                  const price = option.price
                    ? ` - R$ ${parseFloat(String(option.price)).toFixed(2).replace(".", ",")}`
                    : "";
                  return `${option.name}${price}`;
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedProduct}
                onChange={(_, newValue) => setSelectedProduct(newValue)}
                loading={productsLoading}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <span style={{ fontWeight: 500 }}>{option.name}</span>
                      {option.price && (
                        <span style={{ opacity: 0.8 }}>
                          R$ {parseFloat(String(option.price)).toFixed(2).replace(".", ",")}
                        </span>
                      )}
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecionar produto..."
                    variant="outlined"
                    size="small"
                    className={styles.muiFieldInput}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="Nenhum produto encontrado"
                loadingText="Carregando..."
                sx={{ width: "100%" }}
              />
            </label>

            <label>
              <span>Data de Início<span style={{ color: "#e53935" }}>*</span>:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={styles.inputField}
                required
              />
            </label>

            <label>
              <span>Data de Término<span style={{ color: "#e53935" }}>*</span>:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={styles.inputField}
                required
              />
            </label>

            <label>
              <span>Status:</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={styles.inputField}
              >
                <option value="ACTIVE">Ativa</option>
                <option value="CANCELED">Cancelada</option>
              </select>
            </label>
          </div>

          <div className={styles.modalActions}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className={styles.submitButton} disabled={saving}>
              {saving ? "Criando..." : "Criar inscrição"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
